import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Workflow, Plus, Search, MoreVertical, Play, Pause, Copy, Trash2,
  MessageSquare, Phone, Calendar, Users, Building, TrendingUp,
  Clock, CheckCircle, AlertCircle, Edit, Eye, Zap, Brain, Settings,
  GraduationCap, ThumbsUp, ThumbsDown, BarChart3, Filter, RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  published: 'bg-emerald-500',
  draft: 'bg-amber-500',
  disabled: 'bg-slate-500'
};

const statusLabels = {
  published: 'Published',
  draft: 'Draft',
  disabled: 'Disabled'
};

const WorkflowDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [intents, setIntents] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Training tab state
  const [trainingLogs, setTrainingLogs] = useState([]);
  const [trainingAnalytics, setTrainingAnalytics] = useState(null);
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingPage, setTrainingPage] = useState(1);
  const [trainingPagination, setTrainingPagination] = useState(null);
  const [trainingFilters, setTrainingFilters] = useState({
    intent: 'all',
    method: 'all',
    feedback: 'all',
    search: ''
  });
  const [feedbackDialog, setFeedbackDialog] = useState({ open: false, log: null });
  const [feedbackForm, setFeedbackForm] = useState({ isCorrect: true, correctIntent: '', notes: '' });

  useEffect(() => {
    fetchData();
  }, [token]);

  // Progressive — tiles paint as they arrive instead of waiting for all
  // three (the dashboard aggregation is the slowest of the trio).
  const fetchData = () => {
    setLoading(true);
    const tasks = [
      axios.get(`${API}/api/whatsapp-workflows/workflows?token=${token}`)
        .then((r) => setWorkflows(r.data.workflows || []))
        .catch((e) => console.error('[WorkflowDashboard] workflows failed:', e)),
      axios.get(`${API}/api/whatsapp-workflows/intents?token=${token}`)
        .then((r) => setIntents(r.data.intents || []))
        .catch((e) => console.error('[WorkflowDashboard] intents failed:', e)),
      axios.get(`${API}/api/whatsapp-workflows/dashboard?token=${token}`)
        .then((r) => setStats(r.data))
        .catch((e) => console.error('[WorkflowDashboard] dashboard failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(tasks).then(off);
    Promise.allSettled(tasks).finally(() => clearTimeout(safety));
  };

  const fetchTrainingData = async (page = 1) => {
    try {
      setTrainingLoading(true);
      const params = new URLSearchParams({
        token,
        page: page.toString(),
        limit: '20'
      });
      
      if (trainingFilters.intent !== 'all') params.append('intent_filter', trainingFilters.intent);
      if (trainingFilters.method !== 'all') params.append('method_filter', trainingFilters.method);
      if (trainingFilters.feedback !== 'all') params.append('feedback_filter', trainingFilters.feedback);
      if (trainingFilters.search) params.append('search', trainingFilters.search);
      
      const [logsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp-workflows/training/logs?${params}`),
        axios.get(`${API}/api/whatsapp-workflows/training/analytics?token=${token}`)
      ]);
      
      setTrainingLogs(logsRes.data.logs || []);
      setTrainingPagination(logsRes.data.pagination);
      setTrainingAnalytics(analyticsRes.data.analytics);
      setTrainingPage(page);
    } catch (error) {
      console.error('Error fetching training data:', error);
      toast.error('Failed to load training data');
    } finally {
      setTrainingLoading(false);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackDialog.log) return;
    
    try {
      await axios.post(`${API}/api/whatsapp-workflows/training/feedback?token=${token}`, {
        log_id: feedbackDialog.log.id,
        is_correct: feedbackForm.isCorrect,
        correct_intent: feedbackForm.isCorrect ? null : feedbackForm.correctIntent,
        notes: feedbackForm.notes
      });
      
      toast.success(feedbackForm.isCorrect ? 'Marked as correct' : 'Feedback submitted');
      setFeedbackDialog({ open: false, log: null });
      setFeedbackForm({ isCorrect: true, correctIntent: '', notes: '' });
      fetchTrainingData(trainingPage);
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const openFeedbackDialog = (log, isCorrect = null) => {
    setFeedbackForm({
      isCorrect: isCorrect !== null ? isCorrect : true,
      correctIntent: '',
      notes: ''
    });
    setFeedbackDialog({ open: true, log });
  };

  const createDefaults = async () => {
    try {
      const response = await axios.post(`${API}/api/whatsapp-workflows/create-defaults?token=${token}`);
      if (response.data.success) {
        toast.success('Default workflows created successfully');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create default workflows');
    }
  };

  const toggleWorkflow = async (workflowId, currentStatus) => {
    try {
      const enabled = currentStatus !== 'published';
      await axios.post(`${API}/api/whatsapp-workflows/workflows/${workflowId}/toggle?token=${token}`, { enabled });
      toast.success(enabled ? 'Workflow enabled' : 'Workflow disabled');
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };

  const duplicateWorkflow = async (workflowId) => {
    try {
      await axios.post(`${API}/api/whatsapp-workflows/workflows/${workflowId}/duplicate?token=${token}`);
      toast.success('Workflow duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate workflow');
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;
    try {
      await axios.delete(`${API}/api/whatsapp-workflows/workflows/${workflowId}?token=${token}`);
      toast.success('Workflow deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const filteredWorkflows = workflows.filter(wf => {
    const matchesSearch = wf.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         wf.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || wf.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="workflow-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Workflow className="h-7 w-7 text-emerald-500" />
            WhatsApp Workflow Builder
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Create and manage automated WhatsApp conversation flows
          </p>
        </div>
        <div className="flex gap-2">
          {workflows.length === 0 && (
            <Button variant="outline" onClick={createDefaults}>
              <Zap className="h-4 w-4 mr-2" />
              Create Default Flows
            </Button>
          )}
          <Button onClick={() => navigate('/communication/workflow-builder/new')} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-2" />
            New Workflow
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Workflows</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.workflows?.published || 0}
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                  <Workflow className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Mapped Intents</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.intents?.mapped || 0}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <Brain className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Calls</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.call_requests?.pending || 0}
                  </p>
                </div>
                <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <Phone className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Visits</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.visit_requests?.pending || 0}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                  <Calendar className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Tabs defaultValue="workflows" className="space-y-4" onValueChange={(v) => { if (v === 'training') fetchTrainingData(); }}>
        <TabsList>
          <TabsTrigger value="workflows" className="gap-2">
            <Workflow className="h-4 w-4" />
            Workflows ({workflows.length})
          </TabsTrigger>
          <TabsTrigger value="intents" className="gap-2">
            <Brain className="h-4 w-4" />
            Intents ({intents.length})
          </TabsTrigger>
          <TabsTrigger value="requests" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Requests
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-2" data-testid="training-tab">
            <GraduationCap className="h-4 w-4" />
            Training
          </TabsTrigger>
        </TabsList>

        {/* Workflows Tab */}
        <TabsContent value="workflows" className="space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search workflows..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'published', 'draft', 'disabled'].map(status => (
                <Button
                  key={status}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? 'bg-emerald-600' : ''}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>

          {/* Workflow Grid */}
          {filteredWorkflows.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Workflow className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                  No workflows found
                </h3>
                <p className="text-slate-500 text-center mb-4">
                  {searchTerm ? 'Try adjusting your search' : 'Get started by creating your first workflow'}
                </p>
                <Button onClick={() => navigate('/communication/workflow-builder/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workflow
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWorkflows.map(workflow => (
                <Card 
                  key={workflow.id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer group"
                  data-testid={`workflow-card-${workflow.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                          {workflow.name}
                          {workflow.is_default && (
                            <Badge variant="outline" className="text-xs">Default</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="mt-1 line-clamp-2">
                          {workflow.description || 'No description'}
                        </CardDescription>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/communication/workflow-builder/${workflow.id}`)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleWorkflow(workflow.id, workflow.status)}>
                            {workflow.status === 'published' ? (
                              <><Pause className="h-4 w-4 mr-2" />Disable</>
                            ) : (
                              <><Play className="h-4 w-4 mr-2" />Enable</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => duplicateWorkflow(workflow.id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => deleteWorkflow(workflow.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={`${statusColors[workflow.status]} text-white`}>
                          {statusLabels[workflow.status]}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {workflow.nodes?.length || 0} nodes
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="h-3 w-3" />
                        v{workflow.version || 1}
                      </div>
                    </div>
                    
                    {/* Tags */}
                    {workflow.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {workflow.tags.slice(0, 3).map(tag => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Quick Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/communication/workflow-builder/${workflow.id}`)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        variant={workflow.status === 'published' ? 'outline' : 'default'}
                        size="sm" 
                        className={`flex-1 ${workflow.status !== 'published' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                        onClick={() => toggleWorkflow(workflow.id, workflow.status)}
                      >
                        {workflow.status === 'published' ? (
                          <><Pause className="h-3 w-3 mr-1" />Disable</>
                        ) : (
                          <><Play className="h-3 w-3 mr-1" />Enable</>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Intents Tab */}
        <TabsContent value="intents" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-600 dark:text-slate-400">
              Manage AI intents and keyword mappings for workflow triggers
            </p>
            <Button onClick={() => navigate('/communication/workflow-intents')}>
              <Settings className="h-4 w-4 mr-2" />
              Manage Intents
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {intents.map(intent => (
              <Card key={intent.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-white">
                        {intent.display_name}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {intent.training_phrases?.length || 0} phrases, {intent.keywords?.length || 0} keywords
                      </p>
                    </div>
                    <Badge variant={intent.is_active ? 'default' : 'secondary'}>
                      {intent.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  {intent.workflow_name && (
                    <div className="mt-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-md">
                      <p className="text-xs text-slate-500">Mapped to:</p>
                      <p className="text-sm font-medium text-emerald-600">{intent.workflow_name}</p>
                    </div>
                  )}
                  
                  <div className="mt-3 flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {intent.routing_mode}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      Confidence: {(intent.confidence_threshold * 100).toFixed(0)}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Call Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-amber-500" />
                  Call Requests
                </CardTitle>
                <CardDescription>
                  Scheduled calls from workflow automation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <Phone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No pending call requests</p>
                  <Button variant="link" onClick={() => navigate('/communication/workflow-calls')}>
                    View All Calls
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Visit Requests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-500" />
                  Visit Requests
                </CardTitle>
                <CardDescription>
                  Scheduled visits from workflow automation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No pending visit requests</p>
                  <Button variant="link" onClick={() => navigate('/communication/workflow-visits')}>
                    View All Visits
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="space-y-6" data-testid="training-content">
          {trainingLoading && !trainingAnalytics ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <>
              {/* Analytics Cards */}
              {trainingAnalytics && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-l-4 border-l-blue-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Total Classifications</p>
                          <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {trainingAnalytics.total_classifications || 0}
                          </p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-blue-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-emerald-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Accuracy Rate</p>
                          <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {trainingAnalytics.feedback?.accuracy_rate || 0}%
                          </p>
                        </div>
                        <ThumbsUp className="h-8 w-8 text-emerald-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-amber-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Pending Review</p>
                          <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {trainingAnalytics.feedback?.pending_review || 0}
                          </p>
                        </div>
                        <Clock className="h-8 w-8 text-amber-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-purple-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">AI Classifications</p>
                          <p className="text-2xl font-bold text-slate-900 dark:text-white">
                            {trainingAnalytics.by_method?.ai || 0}
                          </p>
                        </div>
                        <Brain className="h-8 w-8 text-purple-500 opacity-50" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filters */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="relative flex-1 min-w-[200px] max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search messages..."
                        value={trainingFilters.search}
                        onChange={(e) => setTrainingFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                        onKeyDown={(e) => e.key === 'Enter' && fetchTrainingData(1)}
                      />
                    </div>
                    
                    <Select
                      value={trainingFilters.intent}
                      onValueChange={(v) => setTrainingFilters(prev => ({ ...prev, intent: v }))}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="All Intents" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Intents</SelectItem>
                        {intents.map(intent => (
                          <SelectItem key={intent.name} value={intent.name}>
                            {intent.display_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={trainingFilters.method}
                      onValueChange={(v) => setTrainingFilters(prev => ({ ...prev, method: v }))}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="All Methods" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Methods</SelectItem>
                        <SelectItem value="ai">AI</SelectItem>
                        <SelectItem value="keyword">Keyword</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select
                      value={trainingFilters.feedback}
                      onValueChange={(v) => setTrainingFilters(prev => ({ ...prev, feedback: v }))}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="All Feedback" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Feedback</SelectItem>
                        <SelectItem value="pending">Pending Review</SelectItem>
                        <SelectItem value="correct">Correct</SelectItem>
                        <SelectItem value="incorrect">Incorrect</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button variant="outline" onClick={() => fetchTrainingData(1)}>
                      <Filter className="h-4 w-4 mr-2" />
                      Apply
                    </Button>
                    
                    <Button variant="ghost" onClick={() => fetchTrainingData(trainingPage)}>
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Classification Logs Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-emerald-500" />
                    Classification Logs
                  </CardTitle>
                  <CardDescription>
                    Review and provide feedback on intent classifications to improve accuracy
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {trainingLogs.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium">No classifications yet</p>
                      <p className="text-sm mt-1">Classifications will appear here as messages are processed</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b bg-slate-50 dark:bg-slate-800">
                              <th className="text-left p-3 font-medium">Message</th>
                              <th className="text-left p-3 font-medium">Detected Intent</th>
                              <th className="text-center p-3 font-medium">Confidence</th>
                              <th className="text-center p-3 font-medium">Method</th>
                              <th className="text-center p-3 font-medium">Status</th>
                              <th className="text-center p-3 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trainingLogs.map((log, idx) => (
                              <tr key={log.id || idx} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                <td className="p-3 max-w-[300px]">
                                  <p className="truncate" title={log.message}>{log.message}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(log.created_at).toLocaleString()}
                                  </p>
                                </td>
                                <td className="p-3">
                                  <Badge variant="outline" className="capitalize">
                                    {log.detected_intent || 'None'}
                                  </Badge>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`font-medium ${
                                    log.confidence >= 0.9 ? 'text-emerald-600' :
                                    log.confidence >= 0.7 ? 'text-amber-600' : 'text-red-600'
                                  }`}>
                                    {((log.confidence || 0) * 100).toFixed(0)}%
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <Badge variant={log.classification_method === 'ai' ? 'default' : 'secondary'}>
                                    {log.classification_method}
                                  </Badge>
                                </td>
                                <td className="p-3 text-center">
                                  {log.feedback ? (
                                    <Badge variant={log.feedback.is_correct ? 'default' : 'destructive'}>
                                      {log.feedback.is_correct ? 'Correct' : 'Incorrect'}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-slate-500">Pending</Badge>
                                  )}
                                </td>
                                <td className="p-3 text-center">
                                  <div className="flex justify-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                      onClick={() => openFeedbackDialog(log, true)}
                                      title="Mark as correct"
                                    >
                                      <ThumbsUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => openFeedbackDialog(log, false)}
                                      title="Mark as incorrect"
                                    >
                                      <ThumbsDown className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Pagination */}
                      {trainingPagination && trainingPagination.pages > 1 && (
                        <div className="flex justify-between items-center mt-4 pt-4 border-t">
                          <p className="text-sm text-slate-500">
                            Page {trainingPagination.page} of {trainingPagination.pages} ({trainingPagination.total} total)
                          </p>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={trainingPage === 1}
                              onClick={() => fetchTrainingData(trainingPage - 1)}
                            >
                              Previous
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={trainingPage >= trainingPagination.pages}
                              onClick={() => fetchTrainingData(trainingPage + 1)}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Analytics Breakdown */}
              {trainingAnalytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Intent Distribution */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Intent Distribution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {trainingAnalytics.by_intent?.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                              <span className="text-sm capitalize">{item.intent}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-slate-500">{item.count} ({((item.avg_confidence || 0) * 100).toFixed(0)}%)</span>
                              <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-emerald-500"
                                  style={{ width: `${(item.count / trainingAnalytics.total_classifications) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Confusion Matrix (Most Confused Intents) */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Most Confused Intents</CardTitle>
                      <CardDescription>Common misclassifications to improve</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {trainingAnalytics.confusion_matrix?.length > 0 ? (
                        <div className="space-y-3">
                          {trainingAnalytics.confusion_matrix.map((item, idx) => (
                            <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-sm">
                                  <Badge variant="outline">{item.detected_as}</Badge>
                                  <span className="text-slate-500">→</span>
                                  <Badge variant="default" className="bg-emerald-600">{item.should_be}</Badge>
                                </div>
                                <span className="text-sm font-medium text-red-600">{item.count}x</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                          <p>No confusion detected!</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Feedback Dialog */}
      <Dialog open={feedbackDialog.open} onOpenChange={(open) => !open && setFeedbackDialog({ open: false, log: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Provide Feedback</DialogTitle>
          </DialogHeader>
          
          {feedbackDialog.log && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Message:</p>
                <p className="text-sm font-medium">{feedbackDialog.log.message}</p>
              </div>
              
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-sm text-slate-500 mb-1">Detected as:</p>
                <Badge variant="outline" className="capitalize">
                  {feedbackDialog.log.detected_intent || 'None'}
                </Badge>
                <span className="ml-2 text-sm text-slate-500">
                  ({((feedbackDialog.log.confidence || 0) * 100).toFixed(0)}% confidence)
                </span>
              </div>
              
              <div className="flex gap-4">
                <Button
                  variant={feedbackForm.isCorrect ? 'default' : 'outline'}
                  className={feedbackForm.isCorrect ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  onClick={() => setFeedbackForm(prev => ({ ...prev, isCorrect: true }))}
                >
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Correct
                </Button>
                <Button
                  variant={!feedbackForm.isCorrect ? 'destructive' : 'outline'}
                  onClick={() => setFeedbackForm(prev => ({ ...prev, isCorrect: false }))}
                >
                  <ThumbsDown className="h-4 w-4 mr-2" />
                  Incorrect
                </Button>
              </div>
              
              {!feedbackForm.isCorrect && (
                <div>
                  <Label className="text-sm">What should it be?</Label>
                  <Select
                    value={feedbackForm.correctIntent}
                    onValueChange={(v) => setFeedbackForm(prev => ({ ...prev, correctIntent: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select correct intent" />
                    </SelectTrigger>
                    <SelectContent>
                      {intents.map(intent => (
                        <SelectItem key={intent.name} value={intent.name}>
                          {intent.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-sm">Notes (optional)</Label>
                <Textarea
                  value={feedbackForm.notes}
                  onChange={(e) => setFeedbackForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional context..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackDialog({ open: false, log: null })}>
              Cancel
            </Button>
            <Button
              onClick={submitFeedback}
              disabled={!feedbackForm.isCorrect && !feedbackForm.correctIntent}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Submit Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowDashboard;
